/**
 * LinkCache — Map-based cache with TTL support for card link data.
 *
 * Prevents redundant API calls when re-hovering the same card within
 * the inactivity timeout window.
 *
 * Requirements: 7.2, 7.6
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VisibleLink {
  readonly linkId: string;
  readonly targetCardId: string;
  readonly linkType: 'related' | 'blocks' | 'blocked-by';
  /** Hop depth: 0 = direct link from active card, 1 = second-hop link */
  readonly depth?: number;
  /** The card this link originates from (for second-hop links) */
  readonly sourceCardId?: string;
}

export interface CacheEntry {
  readonly links: VisibleLink[];
  readonly fetchedAt: number;
}

// ---------------------------------------------------------------------------
// LinkCache
// ---------------------------------------------------------------------------

export class LinkCache {
  private cache: Map<string, CacheEntry> = new Map();

  /**
   * Returns cached links for a card if the entry is fresher than `maxAge` ms.
   * Returns `null` if no entry exists or the entry has expired.
   */
  get(cardId: string, maxAge: number): VisibleLink[] | null {
    const entry = this.cache.get(cardId);
    if (!entry) return null;

    const age = Date.now() - entry.fetchedAt;
    if (age > maxAge) return null;

    return entry.links;
  }

  /**
   * Stores link data for a card with the current timestamp.
   */
  set(cardId: string, links: VisibleLink[]): void {
    this.cache.set(cardId, {
      links,
      fetchedAt: Date.now(),
    });
  }

  /**
   * Removes cached data for a specific card.
   */
  invalidate(cardId: string): void {
    this.cache.delete(cardId);
  }

  /**
   * Removes all cached entries.
   */
  clear(): void {
    this.cache.clear();
  }
}
